import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SourcingEventStatus, useGetSourcingEventQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../common/subscription/useSub";
import { urls } from "../../../urls";
import { ChangeResponsiblePersonButton } from "./ChangeResponsiblePersonButton";
import { DraftNoEditAccessBanner } from "./DraftNoEditAccessBanner";
import { EventHeader } from "./EventHeader";
import { PublicationStatusBanner } from "./PublicationStatusBanner";
import { EventPhaseTimeline } from "./timeline/EventPhaseTimeline";
import { EventTabs, parseEventTab, type EventTab } from "./EventTabs";

// While a notice is awaiting Doffin, poll so the page reflects publication without a manual
// refresh. Bounded by the BFF's own Doffin poll cadence — this only governs how fast the
// already-changed status reaches this open page.
const PENDING_POLL_MS = 15000;

export const SourcingEventView = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = useSub();
  const { selectedOrg } = useLoggedInWithOrgContextState();

  const [pollInterval, setPollInterval] = useState(0);
  const { data: event, isLoading } = useGetSourcingEventQuery(
    sub.hasSourcingEventV2Access && eventId ? { eventId } : skipToken,
    { pollingInterval: pollInterval },
  );
  useEffect(() => {
    setPollInterval(event?.status === SourcingEventStatus.PublicationPending ? PENDING_POLL_MS : 0);
  }, [event?.status]);

  if (!sub.hasSourcingEventV2Access) {
    return (
      <Center py="20">
        <Text color="smMuted">{t("Your organization does not have access to this feature.")}</Text>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  // Gate on missing data, not isError: while polling a pending event a transient poll failure
  // flips isError true with the event still cached — keep rendering rather than blanking the page.
  if (!event) {
    return (
      <Center py="20">
        <Text color="smError">{t("Could not load this sourcing event.")}</Text>
      </Center>
    );
  }

  const isDraft = event.status === SourcingEventStatus.Draft;
  if (isDraft && sub.canCreateEvents) {
    return <Navigate to={urls.publicEvents.edit.go(event.id).info} replace />;
  }

  const activeTab = parseEventTab(searchParams.get("tab"));
  const onTabChange = (tab: EventTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const onViewPublic = () => navigate(urls.portal.view.go(event.id));

  return (
    <Box maxW="container.xl" mx="auto">
      <EventHeader
        event={event}
        onViewPublic={onViewPublic}
        responsibleEditControl={
          event.responsiblePerson && (
            // The buyer view is always scoped to the owning org (the BFF rejects access from other
            // orgs), so the current org is the event owner — the roster to pick a new responsible from.
            <ChangeResponsiblePersonButton
              eventId={event.id}
              ownerOrgId={selectedOrg.id}
              currentResponsible={event.responsiblePerson}
            />
          )
        }
      />
      {isDraft && <DraftNoEditAccessBanner />}
      <PublicationStatusBanner status={event.status} />
      <EventPhaseTimeline event={event} />
      <Box mt="6">
        <EventTabs event={event} activeTab={activeTab} onTabChange={onTabChange} />
      </Box>
    </Box>
  );
};
